import { useState } from "react";
import { Link } from "react-router-dom";
import { Facebook, Instagram, Mail, Phone } from "lucide-react";
import { toast } from "sonner";
import { Emblem } from "./Emblem";
import { api, formatApiError } from "@/lib/api";

const COLUMNS = [
  {
    title: "Services",
    links: [
      { to: "/concierge", label: "Concierge" },
      { to: "/tickets", label: "Tickets" },
      { to: "/events", label: "Events" },
      { to: "/membership", label: "Membership" },
    ],
  },
  {
    title: "Company",
    links: [
      { to: "/about", label: "About" },
      { to: "/membership", label: "Memberships" },
      { to: "/contact", label: "Contact" },
    ],
  },
  {
    title: "Support",
    links: [
      { to: "/contact", label: "FAQs" },
      { to: "/about", label: "Privacy Policy" },
      { to: "/about", label: "Terms" },
    ],
  },
];

export const Footer = () => {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      const { data } = await api.post("/newsletter", { email });
      toast.success(data.message || "Welcome to the inner circle.");
      setEmail("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <footer data-testid="site-footer" className="border-t border-[rgba(200,164,93,0.22)] bg-[#050505]">
      <div className="mx-auto grid max-w-[1280px] gap-14 px-6 py-20 md:grid-cols-2 lg:grid-cols-[1.7fr_1fr_1fr_1fr_1.1fr] lg:px-12 lg:py-24">
        <div>
          <Link to="/" className="flex items-center gap-3" data-testid="footer-logo">
            <Emblem className="h-10 w-auto" />
            <span className="font-serif text-lg font-semibold tracking-[0.16em] text-ivory sm:text-xl">
              MDS ARABAMA
            </span>
          </Link>
          <p className="mt-6 max-w-xs font-serif text-lg font-light italic leading-relaxed text-ivory-muted">
            Redefining access. Creating opportunities. Delivering exceptional.
          </p>
          <p className="mt-9 text-[10px] font-medium uppercase tracking-[0.3em] text-champagne">
            Stay connected
          </p>
          <form onSubmit={subscribe} className="mt-4 flex max-w-xs items-end gap-3">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Your email"
              aria-label="Email address"
              data-testid="footer-newsletter-input"
              className="lux-input flex-1"
            />
            <button
              type="submit"
              disabled={busy}
              data-testid="footer-newsletter-submit"
              className="shrink-0 bg-champagne px-5 py-[11px] text-[9px] font-medium uppercase tracking-[0.24em] text-[#050505] transition-all duration-300 hover:bg-champagne-light disabled:opacity-50"
            >
              Subscribe
            </button>
          </form>
        </div>

        {COLUMNS.map((col) => (
          <nav key={col.title} aria-label={col.title}>
            <p className="text-[10px] font-medium uppercase tracking-[0.3em] text-champagne">
              {col.title}
            </p>
            <ul className="mt-6 space-y-3.5">
              {col.links.map((l) => (
                <li key={l.label}>
                  <Link
                    to={l.to}
                    data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="text-[13px] font-light text-ivory-muted transition-colors duration-300 hover:text-champagne"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        ))}

        <div>
          <p className="text-[10px] font-medium uppercase tracking-[0.3em] text-champagne">
            Connect
          </p>
          <ul className="mt-6 space-y-3.5">
            <li>
              <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" data-testid="footer-social-facebook" className="flex items-center gap-3 text-[13px] font-light text-ivory-muted transition-colors duration-300 hover:text-champagne">
                <Facebook className="h-3.5 w-3.5 text-champagne/80" strokeWidth={1.25} /> Facebook
              </a>
            </li>
            <li>
              <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" data-testid="footer-social-instagram" className="flex items-center gap-3 text-[13px] font-light text-ivory-muted transition-colors duration-300 hover:text-champagne">
                <Instagram className="h-3.5 w-3.5 text-champagne/80" strokeWidth={1.25} /> Instagram
              </a>
            </li>
            <li>
              <a href="mailto:concierge@mdsarabama.com" data-testid="footer-social-email" className="flex items-center gap-3 text-[13px] font-light text-ivory-muted transition-colors duration-300 hover:text-champagne">
                <Mail className="h-3.5 w-3.5 text-champagne/80" strokeWidth={1.25} /> Email
              </a>
            </li>
            <li>
              <a href="tel:+447481952461" data-testid="footer-social-phone" className="flex items-center gap-3 text-[13px] font-light text-ivory-muted transition-colors duration-300 hover:text-champagne">
                <Phone className="h-3.5 w-3.5 text-champagne/80" strokeWidth={1.25} /> +44 7481 952461
              </a>
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-[rgba(255,255,255,0.07)]">
        <div className="mx-auto flex max-w-[1280px] flex-col items-center justify-between gap-4 px-6 py-7 sm:flex-row lg:px-12">
          <p className="text-[11px] font-light tracking-wide text-ivory-muted/70" data-testid="footer-copyright">
            © 2026 MDS Arabama Ltd. All rights reserved.
            <span className="mx-3 text-champagne/50" aria-hidden="true">·</span>
            Designed by&nbsp;
            <a
              href="https://jayalminshawi.com/book"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-designer-credit"
              className="underline underline-offset-4 decoration-champagne/50 transition-colors duration-300 hover:text-champagne hover:decoration-champagne"
            >
              Jay Alminshawi
            </a>
          </p>
          <div className="flex items-center gap-7">
            {["Privacy Policy", "Terms of Service", "Cookie Policy"].map((l) => (
              <Link
                key={l}
                to="/about"
                data-testid={`footer-legal-${l.toLowerCase().replace(/\s+/g, "-")}`}
                className="text-[11px] font-light tracking-wide text-ivory-muted/70 transition-colors duration-300 hover:text-champagne"
              >
                {l}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};
